<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <section class="integrations-page">
      <section class="header" v-if="!activePageContent">
        <h2>{{ $i18nPresenter.t('Integrations') }}</h2>
        <p>{{ $i18nPresenter.t('Connect AhaSlides with your favorite tools!') }}</p>
      </section>
      <section class="header" v-if="activePageContent">
        <div class="breadcrumb">
          <span class="breadcrumb__item">{{ $i18nPresenter.t('Integrations') }}</span>
          <span class="breadcrumb__item--divider">/</span>
        </div>
        <h2>{{ activePageContent.name }}</h2>
      </section>
      <ul class="navigation">
        <router-link
          to="/apps/integrations"
          :exact-active-class="'active'"
          tag="li"
          exact-path
          @click.native="clickExplore"
        >
          <aha-icon
            class="icon"
            name="integrations"
            viewBox="0 0 24 24"
            width="18"
            height="24"
          />
          <span>{{ $i18nPresenter.t('Explore') }}</span>
        </router-link>
        <router-link
          v-for="item in menuItems"
          :key="item.id"
          :to="`/apps/integrations/${item.id}`"
          exact-path
          tag="li"
          :exact-active-class="'active'"
          @click.native="clickMenuOption(item.menuAction)"
        >
          <aha-icon
            class="icon"
            :name="item.icon"
            maintain-color
            :viewBox="item.viewbox"
            width="20"
            height="20"
          />
          <span>{{ item.name }}</span>
        </router-link>
      </ul>
      <section v-if="!$route.params.id" class="content">
        <section
          class="group-content"
          v-for="group in groups"
          :key="group.name"
        >
          <h3>{{ $i18nPresenter.t(group.name) }}</h3>
          <section class="group-items">
            <article
              class="card-item"
              v-for="item in group.items"
              :key="item.name"
              @click="toDetailPage(item.id, item.cardAction, item.name)"
            >
              <img :src="item.imageSrc" width="343" />
              <section class="card-content">
                <section class="content-text flex flex-col gap-4">
                  <h3>
                    <img height="32" width="32" :src="item.iconUrl" />{{ item.name }}
                  </h3>
                  <span>{{ $i18nPresenter.t(item.desc) }}</span>
                </section>
                <aha-antd-button
                  v-aha-emit-action="{
                    eventAction: item.cardAction,
                    eventLabel: item.name,
                  }"
                  name="integration-get-it-now"
                  v-if="!item.isAddBtn"
                  variant="secondary"
                  @click.stop="open(item.url, item.btnAction, item.name)"
                >
                  {{ $i18nPresenter.t(`Add to ${item.name}`) }}
                </aha-antd-button>
                <aha-antd-button
                  v-aha-emit-action
                  name="integration-add"
                  v-if="item.isAddBtn"
                  variant="secondary"
                  @click.stop="open(item.url, item.btnAction, item.name)"
                >
                  {{ $i18nPresenter.t(`Add to ${item.name}`) }}
                </aha-antd-button>
              </section>
            </article>
          </section>
        </section>
      </section>
      <section v-if="$route.params.id" class="content-detail">
        <section class="top">
          <section class="left">
            <img :src="activePageContent.iconUrl" :width="activePageContent.iconWidth || 55" />
            <h3>{{ $i18nPresenter.t(activePageContent.title) }}</h3>
            <span>{{ $i18nPresenter.t(activePageContent.desc) }}</span>
            <aha-antd-button
              v-aha-emit-action="{
                eventAction: activePageContent.btnAction,
                eventLabel: activePageContent.name,
              }"
              name="integration-get-it-now"
              variant="secondary"
              v-if="!activePageContent.isAddBtn"
              @click="open(activePageContent.url, activePageContent.btnAction, activePageContent.name)"
            >
              {{ $i18nPresenter.t(`Add to ${activePageContent.name}`) }}
            </aha-antd-button>
            <aha-antd-button
              v-aha-emit-action
              :name="`integration-add-${activePageContent.name}`"
              v-if="activePageContent.isAddBtn"
              variant="secondary"
              @click="open(activePageContent.url, activePageContent.btnAction, activePageContent.name)"
            >
              {{ $i18nPresenter.t(`Add to ${activePageContent.name}`) }}
            </aha-antd-button>
          </section>
          <section class="right">
            <img
              v-for="guide in activePageContent.guides"
              :key="guide.title"
              :src="guide.imgSrc"
              @click="openGuide(guide)"
              width="343"
            >
          </section>
        </section>
        <section class="bottom">
          <section class="left">
            <section class="flex flex-col gap-4" v-if="activePageContent.news">
              <h3>{{ $i18nPresenter.t('What’s new') }}</h3>
              <ul>
                <li v-for="content in activePageContent.news" :key="content">
                  {{ $i18nPresenter.t(content) }}
                </li>
              </ul>
            </section>
            <section class="flex flex-col gap-4">
              <h3>{{ $i18nPresenter.t('How it works') }}</h3>
              <ul>
                <li v-for="content in activePageContent.howitworks" :key="content">
                  {{ $i18nPresenter.t(content) }}
                </li>
              </ul>
            </section>
          </section>
          <section class="right">
            <h3>{{ $i18nPresenter.t('Helps & Tips') }}</h3>
            <ul>
              <li v-for="tip in activePageContent.tips" :key="tip.text">
                <a :href="tip.link" target="_blank" @click="trackOpenLink(tip)">{{ $i18nPresenter.t(tip.text) }}</a>
                <p>{{ $i18nPresenter.t(tip.desc) }}</p>
              </li>
            </ul>
          </section>
        </section>
      </section>
      <aha-modal
        size="xl"
        v-if="selectedGuide"
        @hide="selectedGuide = null"
      >
        <section class="guide-modal">
          <img :src="selectedGuide.imgSrc" />
          <h2>{{ $i18nPresenter.t(selectedGuide.title) }}</h2>
          <p
            v-if="selectedGuide.desc.length === 1"
            v-html="$i18nPresenter.t(selectedGuide.desc[0])"
          >
          </p>
          <p v-if="selectedGuide.desc.length !== 1">
            <ul>
              <li v-for="desc in selectedGuide.desc" :key="desc" v-html="$i18nPresenter.t(desc)">
              </li>
            </ul>
          </p>
        </section>
      </aha-modal>
    </section>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { groupItems as gptGroupItems, menuItems as gptMenuItems } from '@/constant/integrations-center-gpt.constant'
import { groupItems as nonGptGroupItems, menuItems as nonGptMenuItems } from '@/constant/integrations-center.constant'
import Utils from '@/util'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import { INTEGRATIONS_ACTIONS } from '@/constant/eventActions'
import UnleashService from '@/services/unleash'

import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'

export default {
  name: 'Integrations',
  components: {
    InvitationNotifications,
    PublicAnnouncement,
  },
  data() {
    return {
      selectedGuide: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters('ahaGpt', ['isAhaSlidesGPTEnabled']),

    groups() {
      return this.isAhaSlidesGPTEnabled ? gptGroupItems : nonGptGroupItems
    },
    menuItems() {
      return this.isAhaSlidesGPTEnabled ? gptMenuItems : nonGptMenuItems
    },
    validRoutes() {
      return this.menuItems.map((item) => item.id)
    },
    activePageContent() {
      return this.menuItems.find((item) => item.id === this.$route.params.id)
    },
  },
  async created() {
    if (!this.$route.params.id) {
      return
    }
    if (UnleashService) {
      UnleashService.start()
      await UnleashService.ready
    }
    // Refuse to handle request except the integration method id is in our supported list
    if (!this.validRoutes.includes(this.$route.params.id)) {
      this.$router.push({ name: 'Error404' })
    }
  },
  methods: {
    toDetailPage(id, action, label) {
      window.scrollTo(0, 0)
      const trackData = {
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventAction: action,
        eventLabel: label,
      }
      Utils.trackGA4AndMixpanel(action, trackData)
      this.$router.push({ name: 'IntegrationsDetail', params: { id } })
    },
    open(url, action, label) {
      const trackData = {
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventAction: action,
        eventLabel: label,
      }
      Utils.trackGA4AndMixpanel(action, trackData)

      window.open(url, '_blank')
    },
    openGuide(guide) {
      this.selectedGuide = guide
    },
    clickExplore() {
      const trackData = {
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventAction: INTEGRATIONS_ACTIONS.MENU_EXPLORE,
      }
      Utils.trackGA4AndMixpanel(INTEGRATIONS_ACTIONS.MENU_EXPLORE, trackData)
    },
    clickMenuOption(action) {
      const trackData = {
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventAction: action,
      }
      Utils.trackGA4AndMixpanel(action, trackData)
    },
    trackOpenLink(tip) {
      const platform = this.activePageContent ? this.activePageContent.name : ''
      const eventLabel = `${platform} - ${tip.text}`
      const trackData = {
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventAction: INTEGRATIONS_ACTIONS.TIP_CLICK,
        eventLabel,
      }
      Utils.trackGA4AndMixpanel(INTEGRATIONS_ACTIONS.TIP_CLICK, trackData)
    },
  },
}
</script>
<style scoped lang="scss">
    .integrations-page {
      display: grid;
      grid-template: auto auto / 180px auto;
      padding: 1.5rem 2.5rem 7rem 2.5rem;
      gap: 1rem;

      @media (max-width: 600px) {
        padding: 1rem;
      }

      & > .header {
        grid-column: 1 / 4;
      }

      & > .navigation {
        grid-column: 1 / 2;

        @media (max-width: 600px) {
            display: none;
        }
      }

      & > .content, .content-detail {
        grid-column: 2 / 4;
        @media (max-width: 600px) {
          grid-column: 1 / 4;
        }
      }
    }

    .navigation {
      --card-border: 0.5rem;
      --active-border-color: #6A1EBB;
      --active-color: theme('colors.purple.20');
      display: flex;
      flex-direction: column;
      background-color: white;
      border-radius: var(--card-border);

      & > * {
        padding: 1rem;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: 300ms;

        &:first-child {
          border-radius: var(--card-border) var(--card-border) 0 0;
        }

        &.active, &:hover {
          // border-inline-start: 0.5rem solid var(--active-border-color);
          box-shadow: inset 0.5rem 0 var(--active-border-color);
          background: theme('colors.primary.20');
          font-weight: bold;

          & > svg {
            color: rgba(106, 30, 187);
          }
        }

        &:not(.active):hover {
          background: theme('colors.purple.10');
        }

        & > .icon {
          margin-inline-end: 1rem;
        }
      }
    }

    .header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-block-end: 1rem;
        border-block-end: 1px solid #D3D7E1;
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 2rem;

        @media (min-width: 600px) {
          gap: 1rem;
          border-radius: 0.5rem;
          padding: 1rem;
          background-color: white;
        }

        & > .group-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
    }

    .content-detail {
      display: flex;
      flex-direction: column;
      gap: 2rem;

      @media (min-width: 600px) {
        gap: 1rem;
      }

      & > section {
        background-color: white;
        border-radius: 0.5rem;
        display: flex;
        padding: 1rem;
        @media (max-width: 600px) {
          flex-direction: column;
        }
      }

      & > .top {
        align-items: center;
        gap: 1rem;
        & > * {
          flex: 1;
        }

        & > .left {
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 1rem;

          & > button {
            line-height: initial;
            display: flex;
            gap: 0.2rem;
            @media (min-width: 600px) {
              padding-inline: 3rem;
              align-self: start;
            }
          }
        }

        & > .right {
          display: grid;
          gap: 1rem;

          @media (min-width: 600px) {
            grid-template: 1fr 1fr / 1fr 1fr;
          }

          & > img {
            width: 100%;
            aspect-ratio: 16/9;
            border-radius: 10px;
            cursor: pointer;
          }
        }
      }

      & > .bottom {
        gap: 1rem;
        & > * {
          flex: 1;
        }
        & > .left {
          display: flex;
          flex-direction: column;
          gap: 1rem;

          & > section {
            & > ul {
            list-style-type: disc;

            & > li {
              margin-inline-start: 1rem;
              }
            }
          }
        }

        & > .right {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          background-color: #f2f7ff;
          padding: 1rem;
          border-radius: 0.5rem;

          & > ul {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            & a {
              color: var(--primary-90, #6A1EBB);
              font-weight: 700;
            }
          }
        }
      }
    }

    .group-items {
        display: grid;
        gap: 1rem;

        @media (min-width: 600px) {
            grid-template-columns: repeat(auto-fill, 330px);
        }

    }

    .card-item {
        --card-border: 10px;
        cursor: pointer;
        display: flex;
        flex-direction: column;

        & > .card-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            justify-content: space-between;
            padding: 1rem;
            background-color: white;
            border-radius: 0 0 var(--card-border) var(--card-border);
            box-shadow: 0px 1px 3px 0px #D3D7E1CF;

            & > button {
                display: flex;
                line-height: initial;
                gap: 0.2rem;
                @media (min-width: 600px) {
                    align-self: end;
                }
            }

            & img {
              display: inline-block;
              margin-inline-end: 0.5rem;
            }
        }

        & > img {
            width: 100%;
            background-color: coral;
            border-radius: var(--card-border) var(--card-border) 0 0;
        }
    }

    .guide-modal {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      & ul {
        list-style-type: disc;

        & > li {
          margin-inline-start: 1rem;
        }
      }
    }
</style>
